<template>
    <!-- Your inbox, beside the columns of the board being viewed (CompanyBoard.vue). Its cards are board cards on your own
         hidden inbox board (API routes/inbox.ts): drag one onto a column to move it there, or drag a deck card in to park it
         here whole, with its tags, dates, checklist, comments and files. Clicking a card opens it in the card form
         (CompanyBoard.vue), whose Move goes both ways too, for touch screens. -->
    <!-- Collapsed: a thin strip like a collapsed board column -->
    <div v-if="collapsed" class="kanban-col kanban-col-collapsed inbox-col inbox-col-collapsed" title="Show inbox" @click="setCollapsed(false)">
        <svg-icon icon="arrowsout" class="kanban-colcollapse" />
        <span>Inbox ({{ items.length }})</span>
    </div>
    <div v-else class="kanban-col inbox-col">
        <div class="kanban-colh">
            <svg-icon icon="inbox" class="inbox-col-icon" />
            <span>Inbox</span>
            <span class="kanban-colcnt">{{ items.length }}</span>
            <svg-icon icon="arrowsin" class="kanban-colcollapse" title="Collapse inbox" @click="setCollapsed(true)" />
        </div>
        <div class="kanban-colb">
            <div v-if="loaded && !hasEmployee" class="inbox-col-hint">Your inbox needs a Team Hub employee record linked to your account.</div>
            <template v-else-if="loaded">
                <div v-if="number" class="inbox-col-hint">Text <a :href="'sms:' + number">{{ formatPhone(number) }}</a> to add to your inbox. The first line is the title.</div>
                <draggable class="crm-board-cards inbox-col-cards" :list="items" :group="{ name: 'column', pull: true, put: true }" :sort="false" @change="dropped" :animation="150" ghost-class="kanban-ghost" :empty-insert-threshold="40" :force-auto-scroll-fallback="true" :scroll-sensitivity="120" :scroll-speed="20" :delay="200" :delay-on-touch-only="true" :touch-start-threshold="5">
                    <!-- InboxItem marks a card from here dropped on a deck column (CompanyBoard.vue dragEnd) -->
                    <div v-for="item in items" :key="'inbox' + item.ID" class="crm-board-card" :class="item.Overdue ? 'crm-board-item-overdue' : ''" @click="$emit('open', item)">
                        <img v-if="covers[item.ID]" class="inbox-card-cover" :src="fileUrl(covers[item.ID])" alt="" loading="lazy" />
                        <div v-else-if="item.CardColor" class="item-card-color-cover" :style="'background:' + item.CardColor"></div>
                        <div class="crm-board-item-body">
                            <h3>{{ item.Name }}</h3>
                            <div v-if="item.TagsJSON.length" class="customer-request-tags">
                                <div v-for="(tag, k) in item.TagsJSON" :key="k" :style="'background:' + tagColor(item, tag)">{{ tag }}</div>
                            </div>
                            <div class="customer-request-tags crm-extra-tags">
                                <div v-if="item.TextedByEmployeeID"><svg-icon icon="comment" />Texted</div>
                                <div v-if="item.DateDisplay" :class="item.Overdue ? 'crm-extra-tag-overdue' : 'crm-extra-tag-date'"><svg-icon icon="clock" />{{ item.DateDisplay }}</div>
                                <div v-if="item.CommentCount != '0'"><svg-icon icon="comment" />{{ item.CommentCount }}</div>
                                <div v-if="item.FileCount != '0'"><svg-icon icon="paperclip" />{{ item.FileCount }}</div>
                                <div v-if="item.TotalChecklistItems"><svg-icon icon="checkbox" />{{ item.CheckedChecklistItems }} / {{ item.TotalChecklistItems }}</div>
                                <div v-if="item.HomeBoard" :title="'Mirrored from ' + item.HomeBoard.Name"><svg-icon icon="copy" />{{ item.HomeBoard.Name }}</div>
                                <div v-else-if="item.MirrorBoards.length" :title="'Also on ' + item.MirrorBoards.map((b) => b.Name).join(', ')"><svg-icon icon="copy" />{{ item.MirrorBoards.length }}</div>
                            </div>
                        </div>
                    </div>
                </draggable>
                <div v-if="!items.length" class="inbox-col-empty">Nothing in your inbox. Drag a card here to park it.</div>

                <!-- On a task manager, its owner's open rocks this quarter (yours on yours, Keelan's on Keelan's). Drag one onto a column and the card is the rock. Clicking
                     one opens the rock's form (CompanyBoard.vue, open-rock), which can also add it to a column ("Add to Board").
                     Rocks already on the board aren't listed. -->
                <template v-if="showRocks && rocksLoaded">
                    <div class="inbox-rk-head">
                        <span>{{ rocksOwner ? rocksOwner + "'s Rocks" : "Rocks" }}</span>
                        <span class="kanban-colcnt">{{ rocks.length }}</span>
                    </div>
                    <draggable :list="rocks" :group="{ name: 'column', pull: true, put: false }" :sort="false" :force-auto-scroll-fallback="true" :scroll-sensitivity="120" :scroll-speed="20" :delay="200" :delay-on-touch-only="true" :touch-start-threshold="5">
                        <div v-for="r in rocks" :key="'rock' + r.ID" class="inbox-rk" @click="openRock(r)">
                            <div class="inbox-rk-name">{{ r.Name }}</div>
                            <div class="inbox-rk-meta">
                                <span class="crm-rock-st" :class="rockStatus(r.Status).cls">{{ rockStatus(r.Status).label }}</span>
                                <span v-if="r.MilestoneCount">{{ r.MilestonesDone }}/{{ r.MilestoneCount }} milestones</span>
                                <span v-if="r.TeamRock">Team Rock</span>
                            </div>
                        </div>
                    </draggable>
                    <div v-if="!rocks.length" class="inbox-col-empty">No rocks left to add this quarter</div>
                </template>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { VueDraggableNext } from 'vue-draggable-next';
import SvgIcon from '@/components/SvgIcon.vue';
import { currentQuarter, rockStatus } from '@/rocks';
import { prepareCard } from '@/boardCards';

// The inbox's one column (API companyBoards.ts INBOX_COLUMN), as the card form's column list
const INBOX_COLUMNS = [ { name: 'Inbox' } ];

export default defineComponent({
    name: 'InboxPanel',
    // slug: the board being viewed. showRocks: it's a task manager, which lists its owner's rocks too.
    props: [ 'slug', 'showRocks' ],
    // moved: a card went between this inbox and the board, so the board reloads. open / open-rock: a card or a rock from the
    // Rocks list was clicked (CompanyBoard.vue opens it).
    emits: [ 'moved', 'open', 'open-rock' ],
    components: {
        draggable: VueDraggableNext,
        SvgIcon
    },
    data() {
        return {
            INBOX_COLUMNS,
            loaded: false,
            hasEmployee: false,
            number: null as string | null,
            inboxSlug: null as string | null,
            inboxTags: [] as any[], // [{ name, color }]: the inbox board's, picked up from the cards moved in
            covers: {} as any, // { [card ID]: first image file }
            items: [] as any[],
            collapsed: false,
            rocks: [] as any[],
            rocksLoaded: false,
            rocksOwner: null as string | null // the task manager owner's first name, for the heading
        }
    },
    created() {
        try {
            this.collapsed = localStorage.getItem('boardInboxCollapsed') == '1';
        } catch(e) { /* storage unavailable */ }
        this.load();
        this.loadRocks();
    },
    watch: {
        // The board's flags load after the panel can show
        showRocks() {
            this.loadRocks();
        }
    },
    methods: {
        rockStatus,
        async loadRocks() {
            if(!this.showRocks) {
                return;
            }
            try {
                const data = (await axios.get(`${this.$store.state.apiUrl}/api/rocks/myrocks`, { params: { quarter: currentQuarter(), slug: this.slug } })).data;
                // RockRow marks one dropped on a board column (CompanyBoard.vue dragEnd); the rest are the fields a
                // board card is drawn with, so it shows there until the board reloads
                this.rocksOwner = data.OwnerFirstName || null;
                this.rocks = data.Rocks.map((r: any) => ({
                    ...r, RockRow: true, Rock: { ...r }, TagsJSON: [], MirrorBoards: [], CommentCount: '0', FileCount: '0'
                }));
            } catch(e) {
                console.log(e);
            }
            this.rocksLoaded = true;
        },
        // The board opens it in the rock's form, as it does a rock card
        openRock(r: any) {
            this.$emit('open-rock', r);
        },
        async load() {
            try {
                const data = (await axios.get(`${this.$store.state.apiUrl}/api/inbox`)).data;
                this.hasEmployee = data.HasEmployee;
                this.number = data.Number;
                this.inboxSlug = data.Slug;
                this.inboxTags = data.Tags || [];
                this.covers = data.Covers || {};
                const cards = data.Slug ? (await axios.get(`${this.$store.state.apiUrl}/api/companyboarditems/${data.Slug}`)).data : [];
                this.items = cards.map((card: any) => ({ ...prepareCard(card, null, this.$store.state.monthShortStrings), InboxItem: true }));
            } catch(e) {
                console.log(e);
            }
            this.loaded = true;
        },
        fileUrl(file: any): string {
            return this.$store.state.apiUrl + this.$store.state.spacesUrl + file.SpacesPath;
        },
        // A mirror's tags take their colors from its home board
        tagColor(item: any, tag: string): string {
            return ((item.HomeTags || this.inboxTags).find((t: any) => t.name == tag) || {}).color;
        },
        // Collapsed or not is remembered per browser
        setCollapsed(value: boolean) {
            this.collapsed = value;
            try {
                localStorage.setItem('boardInboxCollapsed', value ? '1' : '0');
            } catch(e) { /* storage unavailable */ }
        },
        // A card dragged in from a board column. On a task manager a rock card goes back to the Rocks list (its rock
        // stays as it is); elsewhere it stays put, as does a meeting item's card, which can't leave the task manager.
        // Any other card moves here whole.
        async dropped(event: any) {
            const card = event.added?.element;
            if(!card || card.InboxItem) {
                return;
            }
            if(card.RockRow) {
                // From the Rocks list just below: it isn't a card yet, so it goes back there
                this.items = this.items.filter((i: any) => i !== card);
                this.loadRocks();
                return;
            }
            try {
                if(card.MeetingItem) {
                    alert('This card is a meeting item, so it can\'t go in your inbox.');
                } else if(card.Rock && !this.showRocks) {
                    alert('This card is a rock, so it can\'t go in your inbox. Rocks go back to the Rocks list on a task manager.');
                } else if(card.Rock) {
                    await axios.delete(`${this.$store.state.apiUrl}/api/companyboarditems/${card.ID}`);
                    this.loadRocks();
                } else {
                    await axios.post(`${this.$store.state.apiUrl}/api/companyboarditems/move`, { Slug: this.slug, ID: card.ID, TargetSlug: this.inboxSlug });
                }
            } catch(e: any) {
                console.log(e);
                alert(e?.response?.data || 'There was an error moving that card to your inbox.');
            }
            this.$emit('moved');
            await this.load();
        }
    }
});
</script>
